import * as THREE from 'three';
import type { PlacedObject } from './content';
import { AssetCache, instantiate, type AssetHandle } from './asset-cache';
import { probeKey } from './probe-keys';

interface ObjectInstance { root: THREE.Object3D; revision: number; assetId: string; alive: boolean; handle?: AssetHandle; instance?: ReturnType<typeof instantiate>; placeholder?: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>; tint: string; error: string }
/** Disposable decorative instances; stable IDs are scoped to a persistent room. */
export class ObjectRuntime {
  private roomID = '';
  private objects = new Map<string, ObjectInstance>();
  revision = 0;
  private captureSignatures = new Map<string,string>();
  constructor(private scene: THREE.Scene, private cache?: AssetCache) {}
  get size(): number { return this.objects.size; }
  get roots(): THREE.Object3D[] { return [...this.objects.values()].map(entry=>entry.root); }
  get ready(): boolean { return [...this.objects.values()].every(entry=>entry.assetId==='builtin_cube_v1'||!!entry.instance); }
  get geometryKey(): string {
    return probeKey([...this.objects.values()].map(entry=>({hash:entry.handle?.template.version.hash??entry.assetId,transform:entry.root.matrix.toArray(),color:entry.tint})).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b))));
  }
  get errors(): string[] { return [...this.objects.values()].filter(o => o.error).map(o => o.error); }
  root(id: string): THREE.Object3D | undefined { return this.objects.get(id)?.root; }
  pick(ray: THREE.Raycaster): string | undefined {
    return this.pickHit(ray)?.id;
  }
  pickHit(ray: THREE.Raycaster): { id: string; distance: number } | undefined {
    const hit = ray.intersectObjects([...this.objects.values()].map(o => o.root), true)[0];
    if (!hit) return;
    for (let node: THREE.Object3D | null = hit.object; node; node = node.parent) for (const [id, entry] of this.objects) if (entry.root === node) return { id, distance: hit.distance };
  }
  private create(object: PlacedObject): ObjectInstance {
    const placeholder = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ roughness: .6, wireframe: object.value.assetId !== 'builtin_cube_v1' }));
    placeholder.castShadow = true; placeholder.receiveShadow = true;
    const root = object.value.assetId === 'builtin_cube_v1' ? placeholder : new THREE.Group(); if (root !== placeholder) root.add(placeholder);
    const entry: ObjectInstance = { root, revision: 0, assetId: object.value.assetId, alive: true, placeholder, tint: object.value.color, error: '' };
    this.scene.add(root);
    if (object.value.assetId !== 'builtin_cube_v1' && this.cache) void this.load(entry);
    return entry;
  }
  private async load(entry: ObjectInstance): Promise<void> {
    let handle: AssetHandle | undefined, instance: ReturnType<typeof instantiate> | undefined;
    try {
      handle = await this.cache!.acquire(entry.assetId); if (handle.template.version.kind !== 'prop') throw new Error('Placed asset is not a prop');
      instance = instantiate(handle.template);
      if (!entry.alive) throw new Error('Stale object load');
      this.dropPlaceholder(entry); entry.handle = handle; entry.instance = instance; entry.root.add(instance.root); this.tint(entry);this.revision++;
    } catch (error) { instance?.dispose(); handle?.release(); if (entry.alive) entry.error = error instanceof Error ? error.message : 'Object unavailable'; }
  }
  private dropPlaceholder(entry: ObjectInstance): void { entry.placeholder?.removeFromParent(); entry.placeholder?.geometry.dispose(); entry.placeholder?.material.dispose(); entry.placeholder = undefined; }
  private tint(entry: ObjectInstance): void {
    const color = new THREE.Color(entry.tint);
    entry.root.traverse(node => {
      if (!(node instanceof THREE.Mesh)) return;
      for (const material of Array.isArray(node.material) ? node.material : [node.material]) if (material instanceof THREE.MeshStandardMaterial) {
        if (material.userData.baseColor === undefined) material.userData.baseColor = material.color.clone();
        material.color.copy(material.userData.baseColor as THREE.Color).multiply(color);
      }
    });
  }
  sync(roomID: string, objects: readonly PlacedObject[]): void {
    if (roomID !== this.roomID) { this.dispose(); this.roomID = roomID; }
    const ids = new Set(objects.map(o => o.id));
    for (const [id, entry] of this.objects) if (!ids.has(id)) { this.drop(entry); this.objects.delete(id);this.captureSignatures.delete(id);this.revision++; }
    for (const object of objects) {
      let entry = this.objects.get(object.id);
      if (entry && entry.assetId !== object.value.assetId) { this.drop(entry); this.objects.delete(object.id); entry = undefined; }
      if (!entry) { entry = this.create(object); this.objects.set(object.id, entry); }
      const signature=JSON.stringify(object.value);
      if(this.captureSignatures.get(object.id)!==signature){this.captureSignatures.set(object.id,signature);this.revision++;}
      entry.root.position.set(object.value.position.x, object.value.position.y, object.value.position.z); entry.root.quaternion.fromArray(object.value.rotation);
      entry.root.scale.set(object.value.scale.x, object.value.scale.y, object.value.scale.z);
      entry.root.updateMatrix();
      if (entry.tint !== object.value.color || entry.revision !== object.revision) { entry.tint = object.value.color; this.tint(entry); entry.revision = object.revision; }
    }
  }
  private drop(entry: ObjectInstance): void { entry.alive = false; entry.root.removeFromParent(); this.dropPlaceholder(entry); entry.instance?.dispose(); entry.handle?.release(); }
  dispose(): void { for (const entry of this.objects.values()) this.drop(entry); this.objects.clear(); this.captureSignatures.clear();this.revision++;this.roomID = ''; }
}
